// Tiny API client for the Fastify server (WP0.3). The Admin talks only to our API (D-004).
// Response shapes come from @enp/domain/api, the same zod schemas the server answers with.

import { type Problem, problemSchema, REQUEST_ID_HEADER } from '@enp/domain/api';
import type { z } from 'zod';
import { getToken } from '../auth/token-store.js';
import { reportReachable } from './reachability.js';

/** `VITE_API_URL`, else the API on port 4000 of whatever host served the Admin (LAN-friendly). */
export function apiBaseUrl(
  env: { VITE_API_URL?: string } = import.meta.env,
  location: Pick<Location, 'protocol' | 'hostname'> = window.location,
): string {
  const configured = env.VITE_API_URL?.trim();
  if (configured) return configured.replace(/\/+$/, '');
  return `${location.protocol}//${location.hostname}:4000`;
}

/** True when `baseUrl` points at this computer (the API then trusts the caller). */
export function isLoopbackApi(baseUrl: string): boolean {
  const host = new URL(baseUrl).hostname.replace(/^\[|\]$/g, '');
  return host === 'localhost' || host === '::1' || /^127\.\d+\.\d+\.\d+$/.test(host);
}

/** An error answer from the API. `problem` is the problem+json body when it had one. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
    readonly problem?: Problem,
    /** The server's request id (`x-request-id`), quoted to support as the error reference. */
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The problem's machine-readable `code`, if any. */
  get code(): string | undefined {
    return this.problem?.code;
  }
}

/** The API answered 2xx, but not in the shape the shared contract describes. */
export class ContractError extends Error {
  constructor(
    readonly path: string,
    readonly issues: string,
  ) {
    super(
      `The server's answer to ${path} was not what this app expects. Reload, or update the app.`,
    );
    this.name = 'ContractError';
  }
}

/** The API could not be reached at all (server down, wrong URL, offline). */
export class NetworkError extends Error {
  constructor(readonly url: string) {
    super(`Can't reach the server at ${url}.`);
    this.name = 'NetworkError';
  }
}

export interface ApiRequest<T = unknown> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Override the stored token (used right after sign-in). */
  token?: string | null;
  /** Extra request headers. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Validates (and types) a successful response. */
  schema?: z.ZodType<T>;
}

/** The message people see for an error body: problem+json `title` (+ `detail`). */
export function errorMessage(
  status: number,
  body: unknown,
): { message: string; problem?: Problem } {
  const parsed = problemSchema.safeParse(body);
  if (!parsed.success) return { message: `Request failed (${status})` };
  const { title, detail } = parsed.data;
  return { message: detail ? `${title}: ${detail}` : title, problem: parsed.data };
}

/**
 * Called when the API answers 401 to the stored session token, with the token it refused:
 * renews the session and returns the new token, or ends it (null). Set by the session
 * keeper (auth/keeper.ts).
 */
type UnauthorizedHandler = (refusedToken: string) => Promise<string | null>;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export async function api<T>(path: string, request: ApiRequest<T> = {}): Promise<T> {
  // The token this request goes out with (send reads the same one), so a 401 can say which
  // token was refused: a session renewed while the request was in flight is not renewed again.
  const sentWith = request.token === undefined ? getToken() : request.token;
  try {
    return await send(path, request);
  } catch (error) {
    // The stored session was refused: renew it once and try again, or let it end.
    if (
      error instanceof ApiError &&
      error.status === 401 &&
      request.token === undefined &&
      unauthorizedHandler &&
      sentWith !== null &&
      getToken() !== null
    ) {
      const renewed = await unauthorizedHandler(sentWith);
      if (renewed) return send(path, { ...request, token: renewed });
    }
    throw error;
  }
}

async function send<T>(path: string, request: ApiRequest<T>): Promise<T> {
  const base = apiBaseUrl();
  const token = request.token === undefined ? getToken() : request.token;
  const headers: Record<string, string> = { Accept: 'application/json', ...request.headers };
  if (request.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${base}${path}`, {
      method: request.method ?? 'GET',
      headers,
      body: request.body === undefined ? undefined : JSON.stringify(request.body),
      signal: request.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    reportReachable(false);
    throw new NetworkError(base);
  }
  reportReachable(true);

  const text = await response.text();
  const body: unknown = text ? safeJson(text) : undefined;
  if (!response.ok) {
    const { message, problem } = errorMessage(response.status, body);
    // The header (B-007), else the body's copy (a proxy may drop unexposed headers).
    const requestId = response.headers.get(REQUEST_ID_HEADER) ?? problem?.requestId;
    throw new ApiError(response.status, message, body, problem, requestId);
  }
  if (!request.schema) return body as T;
  const parsed = request.schema.safeParse(body);
  if (!parsed.success) {
    throw new ContractError(
      path,
      parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
    );
  }
  return parsed.data;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
